import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAnalytics, usePages } from '../hooks/useAnalytics';
import DashboardHeader from '../components/layout/DashboardHeader';
import Navigation from '../components/layout/Navigation';
import PageSelectorModal from '../components/modals/PageSelectorModal';
import DateFilterModal from '../components/modals/DateFilterModal';
import Dashboard from '../components/pages/Dashboard';
import AffiliatePage from '../components/pages/AffiliatePage';
import MonetizedPage from '../components/pages/MonetizedPage';
import styles from '../styles/Team.module.css';

export default function Team() {
  const { user, logout } = useAuth0();
  const [selectedPage, setSelectedPage] = useState('overall');
  const [timeRange, setTimeRange] = useState(1); // Default to Yesterday for All Pages
  const [pendingSelectedPage, setPendingSelectedPage] = useState('overall');
  const [pendingTimeRange, setPendingTimeRange] = useState(1);
  const [isPageSelectorOpen, setIsPageSelectorOpen] = useState(false);
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);

  // Custom hooks for data fetching
  const { pages, isLoading: pagesLoading, error: pagesError } = usePages();
  const { data, isLoading, error, refetch } = useAnalytics(selectedPage, timeRange);

  // Auto-reset date range when navigating back to Dashboard with "All Pages" selected
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isDashboardPage = !currentPath.includes('/affiliate') && !currentPath.includes('/monetized');
    
    if (isDashboardPage && selectedPage === 'overall' && timeRange !== 1) {
      setTimeRange(1);
      setPendingTimeRange(1);
    }
  }, [window.location.pathname, selectedPage, timeRange]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Only update applied state when Apply is clicked
  const handleApplyPage = () => {
    setSelectedPage(pendingSelectedPage);
    // Force timeRange to 1 (Yesterday) when "overall" is selected on Dashboard pages
    if (pendingSelectedPage === 'overall' && !window.location.pathname.includes('/affiliate') && !window.location.pathname.includes('/monetized')) {
      setTimeRange(1);
      setPendingTimeRange(1);
    }
    setIsPageSelectorOpen(false);
  };
  const handleApplyDate = () => {
    setTimeRange(pendingTimeRange);
    setIsDateFilterOpen(false);
  };

  // When opening modals, sync pending state with applied state
  const openPageSelector = () => {
    setPendingSelectedPage(selectedPage);
    setIsPageSelectorOpen(true);
  };
  const openDateFilter = () => {
    setPendingTimeRange(timeRange);
    setIsDateFilterOpen(true);
  };
  const closePageSelector = () => setIsPageSelectorOpen(false);
  const closeDateFilter = () => setIsDateFilterOpen(false);

  return (
    <div className={styles.teamContainer}>
              <DashboardHeader
          user={user}
          onLogout={handleLogout}
          onPageSelectorOpen={openPageSelector}
          onDateFilterOpen={openDateFilter}
          selectedPage={selectedPage}
          pages={pages}
          timeRange={timeRange}
          showPageSelector={true}
        />

      <div className={styles.dashboardLayout}>
        <Navigation />
        <main className={styles.mainContent}>
          <Routes>
            <Route path="/" element={<Dashboard data={data} isLoading={isLoading} error={error} onRetry={refetch} />} />
            <Route path="/affiliate" element={<AffiliatePage data={data} isLoading={isLoading} error={error} onRetry={refetch} />} />
            <Route path="/monetized" element={<MonetizedPage selectedPage={selectedPage} timeRange={timeRange} />} />
          </Routes>
        </main>
      </div>

      <PageSelectorModal
        isOpen={isPageSelectorOpen}
        onClose={closePageSelector}
        pages={pages}
        selectedPage={pendingSelectedPage}
        setSelectedPage={setPendingSelectedPage}
        onApply={handleApplyPage}
      />

      <DateFilterModal
        isOpen={isDateFilterOpen}
        onClose={closeDateFilter}
        timeRange={pendingTimeRange}
        setTimeRange={setPendingTimeRange}
        selectedPage={pendingSelectedPage}
        onApply={handleApplyDate}
        isDashboardPage={!window.location.pathname.includes('/affiliate') && !window.location.pathname.includes('/monetized')}
      />
    </div>
  );
}
